/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * One day, as a list, under a week you can thumb through.
 *
 * The day view used to be a 24-hour timeline. On a desktop that shape earns
 * its keep — it shows overlaps and the gaps between appointments at a glance
 * — but on a phone it is 1440px of mostly empty grid to scroll through to
 * find three appointments, and the empty hours are the majority of what is
 * on screen. A list shows the day's actual content in the first screenful.
 * The timeline shape is still there in the week view, which is where reading
 * gaps is what you came to do.
 *
 * The week strip is the navigation the list loses by not being a grid: seven
 * days with a dot under the ones that have something, so moving to tomorrow
 * is one tap and you can see where the week is busy without leaving the day.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Text, UnstyledButton } from '@/shared/ui';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import { isSameDay, weekDates } from '@features/agenda/utils/agenda-dates';
import { eventColor } from '../event-color';
import { AgendaEventRow } from './AgendaEventRow';

interface MobileDayAgendaProps {
  /** The selected day; its week fills the strip. */
  date: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelectDate: (date: Date) => void;
  onSelect: (event: ExpandedEvent) => void;
}

export const MobileDayAgenda: React.FC<MobileDayAgendaProps> = ({
  date, events, calendars, onSelectDate, onSelect,
}) => {
  const { t } = useTranslation('features/agenda');
  const week = useMemo(() => weekDates(date), [date]);

  const dayEvents = useMemo(
    () => events
      .filter((event) => isSameDay(event.start_at_utc, date))
      .sort((a, b) => a.start_at_utc.localeCompare(b.start_at_utc)),
    [events, date],
  );

  /*
   * Which days of the strip carry a dot. Computed once for the week rather
   * than per cell: a cell asking "does any event fall on me" is seven passes
   * over every event in the range on every render.
   */
  const busyDays = useMemo(() => {
    const days = new Set<string>();
    for (const event of events) days.add(dayjs(event.start_at_utc).format('YYYY-MM-DD'));
    return days;
  }, [events]);

  const selected = dayjs(date);
  const isToday = selected.isSame(dayjs(), 'day');

  return (
    <div
      data-testid="agenda-mobile-day-agenda"
      className="flex h-full flex-col overflow-hidden"
    >
      <div
        data-testid="agenda-week-strip"
        role="tablist"
        aria-label={t('mobile.week')}
        className="grid shrink-0 gap-(--space-1) px-(--space-3) pb-(--space-3)"
        style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
      >
        {week.map((day) => (
          <WeekCell
            key={dayjs(day).format('YYYY-MM-DD')}
            day={day}
            selected={dayjs(day).isSame(selected, 'day')}
            today={dayjs(day).isSame(dayjs(), 'day')}
            busy={busyDays.has(dayjs(day).format('YYYY-MM-DD'))}
            onSelect={() => onSelectDate(day)}
          />
        ))}
      </div>

      <Text
        data-testid="agenda-day-line"
        className="shrink-0 px-(--space-4) pb-(--space-3) text-(length:--text-body-sm) text-[var(--text-secondary)]"
      >
        {[
          isToday ? t('mobile.today') : null,
          selected.format('ddd, D [de] MMMM'),
          /*
           * Zero is spelled out rather than counted. CLDR puts 0 in
           * Portuguese's `one` category, so the plural machinery correctly
           * but unhelpfully renders "0 compromisso"; naming the empty case
           * avoids fighting the rule for every language that has its own
           * version of it.
           */
          dayEvents.length === 0
            ? t('mobile.noAppointments')
            : t('mobile.appointments', { count: dayEvents.length }),
        ].filter(Boolean).join(' · ')}
      </Text>

      <div
        className="min-h-0 flex-1 overflow-y-auto px-(--space-3)"
        style={{ paddingBottom: 'calc(var(--space-16) + env(safe-area-inset-bottom, 0px))' }}
      >
        {dayEvents.length === 0 ? (
          <Text
            data-testid="agenda-day-empty"
            className="block py-(--space-8) text-center text-(length:--text-body-sm) text-[var(--text-tertiary)]"
          >
            {t('mobile.emptyDay')}
          </Text>
        ) : (
          <div className="flex flex-col gap-(--space-2)">
            {dayEvents.map((event) => (
              <AgendaEventRow
                key={`${event.event_id}-${event.occurrence_start}`}
                event={event}
                color={eventColor(event, calendars)}
                meta={event.description || undefined}
                onClick={() => onSelect(event)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface WeekCellProps {
  day: Date;
  selected: boolean;
  today: boolean;
  busy: boolean;
  onSelect: () => void;
}

/**
 * One day of the strip.
 *
 * Today and "the day you are looking at" are two different facts and get two
 * different treatments — the number in wine for today, a solid wine pill for
 * the selection. Collapsing them would leave you unable to tell, on any day
 * but today, which one you are actually reading.
 */
const WeekCell: React.FC<WeekCellProps> = ({ day, selected, today, busy, onSelect }) => {
  const d = dayjs(day);

  return (
    <UnstyledButton
      role="tab"
      aria-selected={selected}
      data-testid={`agenda-week-day-${d.format('YYYY-MM-DD')}`}
      onClick={onSelect}
      className="flex flex-col items-center justify-center gap-(--space-1) rounded-(--radius-md) py-(--space-2)"
      style={{ minHeight: 56, background: selected ? 'var(--seed-base)' : 'transparent' }}
    >
      <Text
        component="span"
        className="text-(length:--text-eyebrow) capitalize leading-none"
        style={{ color: selected ? 'var(--text-inverse)' : 'var(--text-tertiary)' }}
      >
        {d.format('ddd').replace('.', '')}
      </Text>
      <Text
        component="span"
        className="text-(length:--text-body-sm) leading-none tabular-nums"
        style={{
          color: selected
            ? 'var(--text-inverse)'
            : today ? 'var(--seed-base)' : 'var(--text-primary)',
          fontWeight: today || selected
            ? ('var(--font-bold)' as unknown as number)
            : ('var(--font-medium)' as unknown as number),
        }}
      >
        {d.format('D')}
      </Text>
      <Text
        component="span"
        aria-hidden="true"
        className="h-1 w-1 rounded-full"
        style={{
          background: busy
            ? (selected ? 'var(--text-inverse)' : 'var(--seed-base)')
            : 'transparent',
        }}
      />
    </UnstyledButton>
  );
};


export default MobileDayAgenda;
